import { FC, useMemo } from "react";
import { HalfEvent } from "refsix-js-models";
import { Line } from "react-chartjs-2";
import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    LinearScale,
    LineElement,
    PointElement,
} from "chart.js";
import { LiveGraphConfig } from "../replayTypes";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler
);

// A point usable by either the speed graph (GPS plotting points, `timestamp` +
// `speed`) or the heart-rate graph (HrPoint, `time` + `value`).
type GraphPoint = {
    timestamp?: number;
    time?: number;
    speed?: number;
    value?: number;
};

interface ReplayLiveGraphProps {
    points: GraphPoint[];
    matchTimings: HalfEvent[];
    config: LiveGraphConfig;
    currentTimestamp: number;
    // Height of the chart drawing area, matched to the heatmap pitch height.
    height?: number;
}

const DEFAULT_GRAPH_HEIGHT = 150;

const tsToSec = (timestamp: number) => Math.round(timestamp / 1000);
const pointTs = (point: GraphPoint) => point.timestamp ?? point.time ?? 0;

function convertHex(hex: string, opacity: number): string {
    const clean = hex.replace("#", "");
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${opacity / 100})`;
}

/**
 * Builds one sample per second across the whole match, so the rolling window can
 * be sliced by playback position. Ported from LiveStatsGraph._pointsToSeconds.
 */
function pointsToSeconds(
    points: GraphPoint[],
    startTs: number,
    endTs: number,
    maxRepeatSeconds: number,
    propertyToDisplay: "speed" | "value"
): number[] {
    const samples: number[] = [];
    const startSecond = tsToSec(startTs);
    const endSecond = tsToSec(endTs);
    let currentIndex = 0;
    let repeatCount = 0;

    const valueOf = (p?: GraphPoint) =>
        (p && (p[propertyToDisplay] as number)) || 0;

    if (points && points.length > 0) {
        for (let sec = startSecond; sec <= endSecond; sec++) {
            const currentPoint = points[currentIndex];
            const currentPointSecs = tsToSec(pointTs(currentPoint));

            if (currentPointSecs === sec) {
                samples.push(valueOf(currentPoint));
                repeatCount = 0;
            } else if (sec < currentPointSecs) {
                samples.push(0);
                repeatCount = 0;
            } else {
                const nextPoint =
                    currentIndex + 1 < points.length
                        ? points[currentIndex + 1]
                        : undefined;
                if (!nextPoint) {
                    samples.push(0);
                } else if (sec < tsToSec(pointTs(nextPoint))) {
                    if (repeatCount > maxRepeatSeconds) {
                        samples.push(0);
                    } else {
                        samples.push(valueOf(currentPoint));
                    }
                    repeatCount++;
                } else {
                    samples.push(valueOf(nextPoint));
                    repeatCount = 0;
                    currentIndex++;
                }
            }
        }
    }
    return samples;
}

/**
 * A rolling line graph (speed or heart rate) that scrolls with video playback,
 * showing the most recent `lastPoints` seconds. Ported from LiveStatsGraph.
 */
const ReplayLiveGraph: FC<ReplayLiveGraphProps> = ({
    points,
    matchTimings,
    config,
    currentTimestamp,
    height = DEFAULT_GRAPH_HEIGHT,
}) => {
    const lastPoints = config.lastPoints;
    const decimals = config.decimalPlaces ?? 1;
    const round = (val: number) => Number(val.toFixed(decimals));

    const startTs = matchTimings[0]?.timestamp || 0;
    const endTs = matchTimings[matchTimings.length - 1]?.endTime || startTs;

    const pointsBySecond = useMemo(
        () =>
            pointsToSeconds(
                points,
                startTs,
                endTs,
                config.maxRepeatSeconds ?? Infinity,
                config.propertyToDisplay
            ),
        [points, startTs, endTs, config.maxRepeatSeconds, config.propertyToDisplay]
    );

    const startSecond = tsToSec(startTs);
    const currentSecond = tsToSec(currentTimestamp);
    const offset = currentSecond - startSecond;

    // Window of the last `lastPoints` samples ending at the current position.
    const windowValues: number[] = [];
    for (let i = offset - lastPoints; i < offset; i++) {
        windowValues.push(
            i >= 0 && i < pointsBySecond.length ? round(pointsBySecond[i]) : 0
        );
    }

    const currentValue =
        offset >= 0 && offset < pointsBySecond.length
            ? round(pointsBySecond[offset])
            : 0;

    const data = {
        labels: windowValues.map((_, i) => i),
        datasets: [
            {
                data: windowValues,
                backgroundColor: convertHex(config.lineColor, 20),
                borderColor: config.lineColor,
                borderWidth: 2,
                tension: 0.25,
                pointRadius: 0,
                fill: true,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 as const },
        plugins: { legend: { display: false } },
        scales: {
            x: { display: false },
            y: {
                min: config.min,
                max: config.max,
                grid: { color: "#353535" },
                ticks: {
                    stepSize: config.stepSize,
                    callback: config.yAxisLabel
                        ? (value: number | string) =>
                              config.yAxisLabel!(Number(value))
                        : undefined,
                },
            },
        },
    };

    return (
        <div className="replay-live-graph">
            <div className="chart-value">
                {config.displayValue(currentValue)}
            </div>
            <div style={{ height }}>
                <Line data={data} options={options as any} />
            </div>
        </div>
    );
};

export default ReplayLiveGraph;
